import type { CostsState, SessionUsage } from '@orgus/protocol';
import { Flame, SquareTerminal } from '@orgus/ui/icons';
import { useMemo } from 'react';
import { Badge, DataTable, EmptyState, RelativeTime, Tooltip, formatCompact, formatMoney } from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useSessionParam } from '../sessions/useSessionParam.ts';
import { baseModel, formatTokensPerMin } from './model.ts';
import { modelLabel } from '../sessions/labels.ts';

interface Row extends SessionUsage {
  project: string;
  machine: string;
  runaway: boolean;
  tokens: number;
  known: boolean;
}

/** The sessions that cost the most this month; a row opens the session. */
export function TopSessions({ costs }: { costs: CostsState }) {
  const [, openSession] = useSessionParam();
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const known = useOrgus((s) => s.sessions);
  const rows = useMemo<Row[]>(() => {
    const hot = new Map(costs.runaways.filter((r) => r.active).map((r) => [r.sessionId, r.rate]));
    return costs.sessions.map((s) => ({
      ...s,
      // A runaway's Burn now is the rate the runaway banner names, so the two never disagree.
      rate: hot.get(s.sessionId) ?? s.rate,
      project: s.projectId ? (projects[s.projectId]?.name ?? 'Removed project') : 'No project',
      machine: machines[s.machineId]?.name ?? 'a removed machine',
      runaway: hot.has(s.sessionId),
      tokens: s.total.input + s.total.output + s.total.cacheRead + s.total.cacheWrite,
      known: !!known[s.sessionId],
    }));
  }, [costs, projects, machines, known]);

  const columns: Column<Row>[] = [
    {
      key: 'title',
      header: 'Session',
      grow: true,
      text: (r) => `${r.title} ${r.project} ${r.machine}`,
      cell: (r) => (
        <span className="flex min-w-0 flex-col md:flex-row md:items-center md:gap-2">
          <span className="truncate font-medium text-fg">{r.title || 'Untitled session'}</span>
          {/* On a phone the chip becomes a caption under the title, so the title keeps the row. */}
          {r.runaway ? (
            <>
              <span className="flex items-center gap-1 text-caption text-status-attention md:hidden">
                <Flame className="size-3 shrink-0" aria-hidden />
                Runaway
              </span>
              <span className="hidden shrink-0 md:inline-flex">
                <Badge data-chip="" tone="waiting" icon={<Flame />}>
                  Runaway
                </Badge>
              </span>
            </>
          ) : r.partial ? (
            <>
              <span className="text-caption text-fg-subtle md:hidden">Partial, undercounts</span>
              <span className="hidden shrink-0 md:inline-flex">
                <Tooltip content="Undercounts: Orgus is still reading this history, or part of a very long transcript was read from its end only.">
                  <Badge data-chip="" tone="neutral" variant="outline" tabIndex={0}>
                    Partial
                  </Badge>
                </Tooltip>
              </span>
            </>
          ) : null}
        </span>
      ),
    },
    {
      key: 'project',
      header: 'Project',
      hideBelow: 'md',
      text: (r) => r.project,
      sortValue: (r) => r.project,
      cell: (r) => (
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-fg-muted">{r.project}</span>
          <span className="hidden shrink-0 text-caption text-fg-subtle xl:inline">on {r.machine}</span>
        </span>
      ),
    },
    {
      key: 'models',
      header: 'Models',
      hideBelow: 'md',
      text: (r) => Object.keys(r.byModel).map((k) => modelLabel(baseModel(k))).join(' '),
      cell: (r) => (
        <span className="text-fg-muted">
          {[...new Set(Object.keys(r.byModel).map((k) => modelLabel(baseModel(k))))].join(', ')}
        </span>
      ),
    },
    {
      key: 'rate',
      header: 'Burn now',
      type: 'number',
      hideBelow: 'lg',
      sortValue: (r) => r.rate,
      cell: (r) => (r.rate > 0 ? <span className={r.runaway ? 'font-medium text-status-attention' : 'text-fg-muted'}>{formatTokensPerMin(r.rate)}</span> : null),
    },
    {
      key: 'active',
      header: 'Last active',
      type: 'date',
      hideBelow: 'sm',
      sortValue: (r) => r.updatedAt,
      cell: (r) => <RelativeTime ts={r.updatedAt} />,
    },
    { key: 'tokens', header: 'Tokens', type: 'number', hideBelow: 'md', sortValue: (r) => r.tokens, cell: (r) => <span className="text-fg-muted">{formatCompact(r.tokens)}</span> },
    {
      key: 'cost',
      header: 'Cost',
      type: 'number',
      sortValue: (r) => r.total.cost,
      cell: (r) => (
        <Tooltip
          content={
            <span className="flex flex-col">
              {Object.entries(r.byModel).map(([k, t]) => (
                <span key={k} className="num">
                  {modelLabel(baseModel(k))}
                  {k.includes('#') ? ` (${k.split('#')[1]})` : ''}: {formatMoney(t.cost)}
                </span>
              ))}
            </span>
          }
        >
          <span className="font-medium text-fg" tabIndex={0}>
            {formatMoney(r.total.cost)}
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <DataTable
      label="Top sessions this month"
      rows={rows}
      columns={columns}
      rowKey={(r) => r.sessionId}
      initialSort={{ key: 'cost', dir: 'desc' }}
      pageSize={10}
      searchable={{ placeholder: 'Search sessions' }}
      onRowClick={(r) => (r.known ? openSession(r.sessionId) : undefined)}
      rowLabel={(r) => (r.known ? `Open session ${r.title}` : `${r.title} (no longer on its machine)`)}
      empty={<EmptyState icon={<SquareTerminal />} title="No session spend this month" description="Sessions appear here, most expensive first, as soon as they use tokens." />}
    />
  );
}
